import React, { useEffect, useState } from 'react';
import {
  Cog6ToothIcon, ArrowPathIcon, CheckCircleIcon,
  CurrencyDollarIcon, UsersIcon, BellIcon, TicketIcon, ServerIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';
import styles from './SettingsPage.module.css';

// ─── Change Password card ────────────────────────────────────────────────────
function ChangePasswordCard() {
  const [form, setForm]   = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState(null); // { ok: boolean, text: string }

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setMsg(null); };

  const submit = async () => {
    if (!form.current || !form.next) return setMsg({ ok: false, text: 'Fill in all fields.' });
    if (form.next.length < 8)        return setMsg({ ok: false, text: 'New password must be at least 8 characters.' });
    if (form.next !== form.confirm)  return setMsg({ ok: false, text: 'New passwords do not match.' });
    setBusy(true);
    try {
      const res = await adminAPI.changePassword(form.current, form.next);
      setMsg({ ok: true, text: res.message || 'Password updated.' });
      setForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle = {
    width: '100%', borderWidth: 1.5, borderStyle: 'solid', borderColor: 'var(--border)',
    borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 12, boxSizing: 'border-box',
  };

  return (
    <div className={`card ${styles.section}`}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIconBox} style={{ background: '#EF444418', border: '1px solid #EF444430' }}>
          <KeyIcon style={{ width: 18, height: 18, color: '#EF4444' }} />
        </div>
        <h2 className={styles.sectionTitle}>Change Password</h2>
      </div>
      <p className={styles.desc} style={{ marginTop: -6, marginBottom: 14 }}>
        Update the password for your own admin account.
      </p>

      <input type="password" autoComplete="current-password" placeholder="Current password"
        style={fieldStyle} value={form.current} onChange={e => set('current', e.target.value)} />
      <input type="password" autoComplete="new-password" placeholder="New password (min 8 chars)"
        style={fieldStyle} value={form.next} onChange={e => set('next', e.target.value)} />
      <input type="password" autoComplete="new-password" placeholder="Confirm new password"
        style={fieldStyle} value={form.confirm} onChange={e => set('confirm', e.target.value)}
        onKeyDown={e => e.key === 'Enter' && submit()} />

      {msg && (
        <div style={{ fontSize: 12, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5,
          color: msg.ok ? 'var(--success)' : 'var(--danger)' }}>
          {msg.ok && <CheckCircleIcon style={{ width: 14, height: 14 }} />}{msg.text}
        </div>
      )}

      <button className="btn-primary" onClick={submit} disabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
        {busy ? <><ArrowPathIcon style={{ width: 14, height: 14 }} /> Updating…</> : <><KeyIcon style={{ width: 14, height: 14 }} /> Update Password</>}
      </button>
    </div>
  );
}

const SECTIONS = [
  {
    title: 'Membership',
    icon: CurrencyDollarIcon,
    color: '#10B981',
    keys: ['membership_price', 'discount_percent', 'membership_duration'],
  },
  {
    title: 'Referral Program',
    icon: UsersIcon,
    color: '#2B9DD4',
    keys: ['referral_threshold', 'referral_reward_value'],
  },
  {
    title: 'Expiry Reminders',
    icon: BellIcon,
    color: '#F59E0B',
    keys: ['reminder_days_1', 'reminder_days_2'],
  },
  {
    title: 'Welcome Coupon',
    icon: TicketIcon,
    color: '#2B9DD4',
    keys: ['welcome_coupon_pct', 'welcome_coupon_days'],
  },
  {
    title: 'System',
    icon: ServerIcon,
    color: '#64748B',
    keys: ['otp_dev_mode'],
  },
];

export default function SettingsPage() {
  const [settings, setSettings] = useState({});   // { key: { ...row } }
  const [values,   setValues]   = useState({});   // { key: value (string) }
  const [dirty,    setDirty]    = useState({});   // { key: true }
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState('');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    adminAPI.getSettings().then(res => {
      const map = {};
      const vals = {};
      (res.settings || []).forEach(s => { map[s.key] = s; vals[s.key] = s.value; });
      setSettings(map);
      setValues(vals);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleChange = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
    setDirty(prev => ({ ...prev, [key]: true }));
    setMsg('');
  };

  const handleSave = async () => {
    const changed = Object.keys(dirty).filter(k => dirty[k]).map(k => ({ key: k, value: values[k] }));
    if (!changed.length) return;
    setSaving(true);
    setMsg('');
    try {
      const res = await adminAPI.saveSettings(changed);
      const map = {};
      const vals = {};
      (res.settings || []).forEach(s => { map[s.key] = s; vals[s.key] = s.value; });
      setSettings(map);
      setValues(vals);
      setDirty({});
      setMsg('Settings saved successfully!');
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setMsg('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.values(dirty).filter(Boolean).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Configure your VIP membership program — changes take effect immediately.</p>
        </div>
        <div className="page-header-actions">
          {dirtyCount > 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
              {dirtyCount} unsaved change{dirtyCount > 1 ? 's' : ''}
            </span>
          )}
          {msg && (
            <span style={{ fontSize: 12, color: msg.includes('success') ? 'var(--success)' : 'var(--danger)', display: 'flex', alignItems: 'center', gap: 4 }}>
              {msg.includes('success') && <CheckCircleIcon style={{ width: 14, height: 14 }} />}
              {msg}
            </span>
          )}
          <button className="btn-primary" onClick={handleSave} disabled={saving || dirtyCount === 0}>
            {saving ? <><ArrowPathIcon style={{ width: 14, height: 14 }} /> Saving…</> : <><Cog6ToothIcon style={{ width: 14, height: 14 }} /> Save Changes</>}
          </button>
        </div>
      </div>

      <div className={styles.grid}>
        {SECTIONS.map(({ title, icon: Icon, color, keys }) => (
          <div key={title} className={`card ${styles.section}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIconBox} style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
                <Icon style={{ width: 18, height: 18, color }} />
              </div>
              <h2 className={styles.sectionTitle}>{title}</h2>
            </div>

            {loading ? (
              keys.map(k => <div key={k} className="skeleton" style={{ height: 58, borderRadius: 10, marginBottom: 14 }} />)
            ) : (
              keys.map(key => {
                const s = settings[key];
                if (!s) return null;
                const isDirty = !!dirty[key];
                return (
                  <div key={key} className={`${styles.field} ${isDirty ? styles.fieldDirty : ''}`}>
                    <div className={styles.fieldHeader}>
                      <label className={styles.label}>{s.label}</label>
                      {isDirty && <span className={styles.dirtyDot} title="Unsaved" />}
                    </div>
                    {s.description && <p className={styles.desc}>{s.description}</p>}
                    {s.type === 'boolean' ? (
                      <div className={styles.toggleRow}>
                        <span className={styles.toggleLabel}>{values[key] === 'true' ? 'Enabled' : 'Disabled'}</span>
                        <label className={styles.toggle}>
                          <input
                            type="checkbox"
                            checked={values[key] === 'true'}
                            onChange={e => handleChange(key, e.target.checked ? 'true' : 'false')}
                          />
                          <span className={styles.toggleSlider} />
                        </label>
                      </div>
                    ) : (
                      <div className={styles.inputRow}>
                        <input
                          className={styles.input}
                          type={s.type === 'number' ? 'number' : 'text'}
                          value={values[key] ?? ''}
                          onChange={e => handleChange(key, e.target.value)}
                          min={0}
                        />
                        {key.includes('price') || key.includes('value') ? <span className={styles.unit}>USD</span>
                          : key.includes('percent') || key.includes('pct') ? <span className={styles.unit}>%</span>
                          : key.includes('days') || key.includes('duration') ? <span className={styles.unit}>days</span>
                          : key.includes('threshold') ? <span className={styles.unit}>referrals</span>
                          : null}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ))}

        {/* System info card */}
        <div className={`card ${styles.section}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIconBox} style={{ background: '#F1F5F9', border: '1px solid #E2E8F0' }}>
              <ServerIcon style={{ width: 18, height: 18, color: '#64748B' }} />
            </div>
            <h2 className={styles.sectionTitle}>System Info</h2>
          </div>
          {[
            ['API Version', '1.0.0'],
            ['Database', 'Neon PostgreSQL'],
            ['Environment', process.env.NODE_ENV || 'development'],
            ['OTP Mode', settings.otp_dev_mode?.value === 'true' ? 'Dev (code: 123456)' : 'Production'],
            ['Scheduler', 'Active — daily at 9AM'],
          ].map(([k, v]) => (
            <div key={k} className={styles.infoRow}>
              <span className={styles.infoKey}>{k}</span>
              <span className={styles.infoVal}>{v}</span>
            </div>
          ))}
        </div>

        {/* Change Password */}
        <ChangePasswordCard />
      </div>
    </div>
  );
}
